import { getI18n } from 'react-i18next';
import { isAxiosError } from 'axios';
import { getAuth } from 'firebase/auth';
import { defer, exhaustMap, filter, map, switchMap } from 'rxjs';

import { Api, whoAmIApi } from '@mopla/data-models';
import { SUPPORTED_LNGS, SupportedLngs } from '@mopla/i18';
import { Logger } from '@mopla/logger';

import {
	checkBannedEmailResult,
	deleteCurrentUserResult,
	fetchUser,
	FetchUserAction,
	fetchUserResult,
	inquiryInvoiceResult,
	saveUserLanguageResult,
	signOut,
	signOutResult,
	TCheckBannedEmailAction,
	TInquiryInvoiceAction,
	TSaveUserLanguageAction,
	UserActionTypes,
} from '../actions/userActions';
import { Effect } from '../business-logic';
import { ofType } from '../operators/ofType';
import { personalDetailsManager } from '../services/personalDetailsManager';
import { unauthorizedError$ } from '../subjects/auth';
import { channelLogoutRunning } from '../subjects/logout';
import { wipeFEStorages } from '../utils/wipeFEStorages';

export const saveUserLanguage = (api: Api, language: string) => {
	return api.patch('/api/users/language', { language });
};

export const fetchUserEffect: Effect<FetchUserAction> = (
	actions$,
	dependencies
) =>
	actions$.pipe(
		ofType(UserActionTypes.FetchUser),
		switchMap(() =>
			defer(async () => {
				try {
					const i18n = getI18n();
					const { data: user } = await whoAmIApi();

					/** Set system language from BE if was saved before. Otherwise, call API to save current lang */
					if (
						user.language &&
						SUPPORTED_LNGS.includes(user.language as SupportedLngs)
					) {
						i18n
							.changeLanguage(user.language)
							.catch((err) => console.error(err));
					} else {
						user.language = i18n.language;

						await saveUserLanguage(dependencies.api, i18n.language);
					}

					await dependencies.db.upsertLocal('user', user);

					dependencies.Sentry.setUser({
						id: user.userId,
					});

					// this is a quick fix to recover from a broken state in localStorage
					// after a user got a wrong entry when entering PersonalDetailsForm
					// TODO this can be removed in a future release as the problem will vanish
					// after some time
					if (!user.pleaseComplete) {
						personalDetailsManager.set('completed');
					}

					return fetchUserResult();
				} catch (error: any) {
					dependencies.Sentry.captureException(error);

					return fetchUserResult({ error });
				}
			})
		)
	);

export const setUserLanguageEffect: Effect = (actions$, dependencies) =>
	actions$.pipe(
		ofType(UserActionTypes.SaveUserLanguage),
		switchMap((action: TSaveUserLanguageAction) =>
			defer(async () => {
				try {
					await saveUserLanguage(dependencies.api, action.payload.language);

					actions$.next(fetchUser());
				} catch (err) {
					dependencies.Sentry.captureException(err);
				}

				return saveUserLanguageResult();
			})
		)
	);

export const inquiryInvoiceEffect: Effect = (actions$, dependencies) =>
	actions$.pipe(
		ofType(UserActionTypes.InquiryInvoice),
		exhaustMap((action: TInquiryInvoiceAction) =>
			defer(async () => {
				const { year, month, subscriptionId } = action.payload;
				try {
					await dependencies.api.post('/api/passengers/invoices', {
						subscriptionId,
						month,
						year,
					});

					return inquiryInvoiceResult();
				} catch (error: any) {
					const isExpectedHttpError =
						isAxiosError(error) &&
						error?.status &&
						[404, 400].includes(error.status);

					if (!isExpectedHttpError) {
						dependencies.Sentry.captureException(error);
					}

					return inquiryInvoiceResult({ error });
				}
			})
		)
	);

export const checkBannedEmailEffect: Effect = (actions$, dependencies) =>
	actions$.pipe(
		ofType(UserActionTypes.CheckBannedEmail),
		exhaustMap((action: TCheckBannedEmailAction) =>
			defer(async () => {
				const { email } = action.payload;
				try {
					const response = await dependencies.api.get(
						`/api/emails/check?email=${encodeURIComponent(email)}`
					);

					return checkBannedEmailResult({ isValid: response.isValid });
				} catch (error: any) {
					dependencies.Sentry.captureException(error);

					return checkBannedEmailResult({ error });
				}
			})
		)
	);

export const signOutEffect: Effect = (action$, deps) => {
	return action$.pipe(
		ofType(UserActionTypes.SignOut),
		exhaustMap(() =>
			defer(async () => {
				await channelLogoutRunning.postMessage({
					action: 'logout_running',
					phase: 'started',
				});

				try {
					await deps.api.post('/api/passengers/deleteTokens');

					await getAuth().signOut();

					await wipeFEStorages(deps);

					await channelLogoutRunning.postMessage({
						action: 'logout_running',
						phase: 'ended',
					});

					/** A page reload should be run after this action
					 * @see LogoutWrapper.tsx
					 * */
					return signOutResult();
				} catch (error: any) {
					deps.Sentry.captureException(error);

					return signOutResult({ error });
				}
			})
		)
	);
};

/**
 * Effect that handles 401 Unauthorized errors from the API interceptors.
 *
 * A 401 is NOT always a permanently-invalid session: it also happens for a
 * transient/stale bearer (a slow token refresh, a backend hiccup, or a request
 * that went out without a token). Logging the user out on those transient 401s
 * force-logs-out users and wipes their local data (MC-11571).
 *
 * So before logging out we verify the session is really gone by forcing a token
 * refresh:
 *  - success -> transient 401, keep the session;
 *  - a network error during refresh -> transient, keep the session;
 *  - a genuine auth error (revoked/expired/disabled, e.g. after a password
 *    reset) -> log out.
 *
 * Separately, the 401s that should *never* happen are reported as Sentry events
 * (not just warn breadcrumbs), once per reason + endpoint per app run, so the
 * cause of an unexpected logout is visible (MC-11571 AC):
 *  - `no-bearer`           -> our token plumbing failed to attach a bearer;
 *  - `valid-token-rejected`-> the backend rejected a token Firebase still
 *                             considers valid (refresh succeeded).
 * Network/offline 401s stay breadcrumbs. A received 401 means the request
 * reached the backend, so there is no `navigator.onLine` gate.
 *
 * Only runs while a user is currently authenticated in Firebase.
 */
export const handle401Effect: Effect = () => {
	const logger = Logger.getInstance('Auth');
	/** Reasons already reported this app run, keyed by `${reason}:${path}`. */
	const reported = new Set<string>();

	return unauthorizedError$.pipe(
		filter(() => getAuth().currentUser !== null),
		exhaustMap((context) =>
			defer(async () => {
				const currentUser = getAuth().currentUser;
				if (!currentUser) {
					return true;
				}

				// Force a token refresh to find out whether the session is really
				// gone. The outcome is also the signal we report on.
				let verdict: 'refresh-ok' | 'network-error' | 'auth-error';
				let refreshError: unknown;
				try {
					await currentUser.getIdToken(true);
					verdict = 'refresh-ok';
				} catch (error) {
					refreshError = error;
					const code: string = (error as { code?: string } | null)?.code ?? '';
					verdict =
						code === 'auth/network-request-failed'
							? 'network-error'
							: 'auth-error';
				}

				// Report only the 401s that shouldn't happen, once per reason +
				// endpoint per app run. Network/offline cases stay breadcrumbs.
				const reason = !context.hadBearer
					? 'no-bearer'
					: verdict === 'refresh-ok'
					? 'valid-token-rejected'
					: null;
				if (reason) {
					const key = `${reason}:${context.path ?? ''}`;
					if (!reported.has(key)) {
						reported.add(key);
						logger.error(`Unexpected backend 401: ${reason}`, undefined, {
							...context,
							nowSec: Math.floor(Date.now() / 1000),
							tokenUserMatches: context.tokenUserId === currentUser.uid,
						});
					}
				}

				if (verdict === 'refresh-ok') {
					return false;
				}
				if (verdict === 'network-error') {
					logger.warn('Ignoring 401; token refresh network error', {
						reason: 'auth/network-request-failed',
					});
					return false;
				}

				logger.error(
					'401 confirmed invalid session; logging out',
					refreshError
				);
				return true;
			})
		),
		filter((shouldLogout) => shouldLogout),
		map(() => signOut())
	);
};

export const deleteCurrentUserEffect: Effect = (action$, deps) => {
	return action$.pipe(
		ofType(UserActionTypes.DeleteCurrentUser),
		exhaustMap(() =>
			defer(async () => {
				await channelLogoutRunning.postMessage({
					action: 'logout_running',
					phase: 'started',
				});

				try {
					await deps.api.post('/api/passengers/deleteTokens');
					await deps.api.post('/api/passengers/delete');

					await getAuth().signOut();

					await wipeFEStorages(deps);

					await channelLogoutRunning.postMessage({
						action: 'logout_running',
						phase: 'ended',
					});

					/** A page reload should be run after this action
					 * @see LogoutWrapper.tsx
					 * */
					return deleteCurrentUserResult();
				} catch (error: any) {
					deps.Sentry.captureException(error);

					return deleteCurrentUserResult({ error });
				}
			})
		)
	);
};
